import ClearIconMUI from '@mui/icons-material/Clear';
import SearchIconMUI from '@mui/icons-material/Search';
import cn from 'classnames';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { ReactComponent as SearchIcon } from '../../assets/images/icon-search.svg';

import { IconButton, InputAdornment, TextField, useTheme } from '@mui/material';
import { Input } from '../../components/forms/inputs/Input';
import { IInputProps } from '../../components/forms/inputs/Input/Input';

import styles from './useSearchFilter.module.scss';

export interface IExternalInputProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  dataAction?: string;
}

interface ISearchFilterInterface<T> {
  items: T[] | null | undefined;
  fieldNames: (keyof T)[];
  props?: IInputProps;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  error?: any;
  ExternalInput?: React.ElementType<IExternalInputProps> | null;
  isMUIDesign?: boolean;
}

export default function useSearchFilter<T>({
  items,
  fieldNames,
  props,
  onChange,
  error,
  ExternalInput,
  isMUIDesign = false,
}: ISearchFilterInterface<T>) {
  const { sass } = useTheme();
  const [query, setQuery] = useState<string>('');
  const [debounced, setDebounced] = useState<string>('');

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (typeof onChange === 'function') {
        onChange(e);
      }
      setQuery(e.currentTarget.value);
    },
    [onChange],
  );

  const { hint, className, dataAction } = props || {};

  const resetSearch = useCallback(() => {
    setQuery('');
    setDebounced('');
  }, []);

  const SearchInput = useMemo(() => {
    return ExternalInput ? (
      <ExternalInput
        key="stable-search"
        value={query}
        onChange={handleChange}
        onClear={resetSearch}
        dataAction={dataAction}
      />
    ) : isMUIDesign ? (
      <TextField
        id="outlined-basic"
        size="small"
        label="Search"
        variant="outlined"
        value={query}
        onChange={handleChange}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {query ? (
                  <IconButton size="small" onClick={resetSearch} data-action="clear_icon">
                    <ClearIconMUI fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIconMUI />
                )}
              </InputAdornment>
            ),
            sx: { width: 286 },
          },
          htmlInput: {
            'data-action': dataAction,
          },
        }}
        sx={{
          backgroundColor: sass.white,
          height: 'fit-content',
        }}
      />
    ) : (
      <Input
        key="stable-search"
        className={cn(styles.search, className)}
        value={query}
        icon={<SearchIcon title={typeof hint === 'string' ? hint : undefined} />}
        onChange={handleChange}
        dataAction={dataAction}
        placeholder="Search"
      />
    );
  }, [query, handleChange, resetSearch, dataAction, ExternalInput, isMUIDesign, hint, className]);

  const filteredItems = useMemo(() => {
    if (!items || !debounced.trim()) {
      return items || [];
    }

    const searchQuery = debounced.toLowerCase();

    const getCombinedText = (item: (typeof items)[0]) =>
      fieldNames
        .map((name) => item[name])
        .filter((v) => typeof v === 'string')
        .join(' ')
        .toLowerCase();

    return items.filter((item) => {
      const hasMatchInField = fieldNames.some((name) => {
        const fieldValue = item[name];
        return typeof fieldValue === 'string' && fieldValue.toLowerCase().includes(searchQuery);
      });

      return hasMatchInField || getCombinedText(item).includes(searchQuery);
    });
  }, [items, fieldNames, debounced]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebounced(query);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [query]);

  return {
    filteredItems,
    SearchInput,
    query: debounced,
    error,
    resetSearch,
  };
}
